import { GTMEndGame } from '../constants/analytics/GTM'
import { useModal } from './useModal'
import useStateControls from './useStateControls'
import { useRequestsHandlerData } from './useRequestsHandlerData'
import { gameRating, gamesList } from '../constants/copyright'
import { useContent } from '../redux/reducer/content'
import {
  RELOAD_REASONS,
  reloadApplication,
} from '../components/features/errorBoundary/ErrorBoundary'

export const useGamesModal = ({
  game,
  callbacks,
  setGameState,
  loseCallback,
  resetCallback,
  resetBoostersCount,
}) => {
  const { gamesSettings, gameRating: gameRatingData } = useContent()
  const stateCallbacks = useStateControls()
  const { setState } = stateCallbacks
  const { addModal } = useModal()
  const { list } = gamesList

  const toGames = () => {
    setState('games')
  }

  const pauseToggle = () => {
    callbacks.gamePause()

    setGameState('playing')
  }

  const leaveModalFunc = () =>
    addModal({
      type: 'leaveModal',
      props: {
        backFunc: () => {
          loseCallback()
          GTMEndGame(game, 0)
        },
        continueFunc: pauseToggle,
        setState: toGames,
      },
    })

  const pauseModalFunc = () => {
    callbacks.gamePause()

    addModal({
      type: 'pauseModal',
      props: {
        backFunc: leaveModalFunc,
        continueFunc: pauseToggle,
      },
    })
  }

  const reloadApp = () => {
    reloadApplication(RELOAD_REASONS.GAME_END_NETWORK_ERROR)
  }

  const isPending = useRequestsHandlerData([
    {
      address: 'requestsGameStart',
      pending: () => resetBoostersCount?.(),
      fulfilled: ({ log }) => {
        const { save_progress_interval, session } = log
        const gameData = { interval: save_progress_interval, session }
        callbacks?.setGameDataProgress(gameData)

        if (resetCallback) {
          resetCallback()
          return
        }

        setGameState('reset')
      },
    },
    {
      address: 'requestsGameEnd',
      fulfilled: ({
        meta: {
          arg: { hash },
        },
        log,
        newPrizes = [],
      }) => {
        const { isWin, score, money_collected, type: gameApiName } = log

        const gameKeyFromLog = Object.keys(list).find((key) => list[key].apiName === gameApiName)
        const resolvedGame = gameKeyFromLog || game

        GTMEndGame(resolvedGame, Number(isWin))
        const gameInfo = list[resolvedGame]
        const { isRating, apiName: name } = gameInfo || {}

        const buttonLink = gamesSettings?.find(({ name: gameName }) => gameName === name)?.shopLink

        isRating &&
          callbacks.getGameRating(name, {
            isSendGameRating: !gameRatingData?.[name],
            isSendUserGameRating: true,
          })

        let coinsReward
        let xpReward

        if (newPrizes.length === 1) {
          const prize = newPrizes[0]
          coinsReward = typeof prize?.money === 'number' ? prize?.money : money_collected
          xpReward = prize?.score ?? null
        } else {
          const xpValue = newPrizes
            .map((prize) => prize?.score)
            .filter((value) => typeof value === 'number')

          xpReward = xpValue.length > 0 ? xpValue.reduce((acc, v) => acc + v, 0) : null

          const coinsValue = newPrizes
            .map((prize) => prize?.money)
            .filter((value) => typeof value === 'number')

          coinsReward = coinsValue.length > 0 ? coinsValue.reduce((acc, v) => acc + v, 0) : null
        }

        addModal({
          type: 'gameResultModal',
          props: {
            callbacks: stateCallbacks,
            gameType: name,
            coins: coinsReward,
            score: hash && score,
            xp: xpReward,
            buttonLink,
            toGames,
          },
        })
      },
      rejected: () => {
        setState('games')

        addModal({
          id: 'onboardingModal',
          type: 'onboardingModal',
          props: {
            title: 'networkErrorModal.modalTitle',
            description: 'networkErrorModal.modalDescription',
            buttons: [
              {
                attr: {
                  text: 'networkErrorModal.reloadButton',
                  disposable: true,
                  modalData: { action: 'close', data: { type: 'current' } },
                  customCallbacks: { reloadApp },
                  callbackKey: ['reloadApp'],
                },
              },
            ],
          },
        })
      },
    },
    {
      address: 'requestsGameGetUserGameRating',
      fulfilled: ({ meta: { arg: gameName }, rating }) => {
        if (!rating) return

        const { position } = rating

        const { isRating } = list[game]

        if (isRating && position <= gameRating.ratingCount)
          callbacks.getGameRating(gameName, { isSendGameRating: true, isSendUserGameRating: false })
      },
    },
  ])

  return { openPauseModal: pauseModalFunc, openLeaveModal: leaveModalFunc, isPending }
}
